import { BreakpointObserver } from '@angular/cdk/layout';
import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, inject } from '@angular/core';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Router } from '@angular/router';
import { IframeDialogComponent } from '@components';
import { FacetValue, loadClickAnalyticsActions } from '@coveo/headless';
import { CoveoInitService, SearchEngineService } from '@services';
import { catchError, firstValueFrom, map, of, Subscription } from 'rxjs';
import { GknRoutePath } from 'src/app/app-routing.enum';
import { RecentlyViewedItem } from '../services/recently-viewed-item.interface';
import { RecentlyViewedService } from '../services/recently-viewed.service';

@Component({
    selector: 'app-recently-viewed-facet',
    templateUrl: './recently-viewed-facet.component.html',
    styleUrls: ['./recently-viewed-facet.component.scss'],
    imports: [CommonModule, MatDialogModule, MatIconModule, MatTooltipModule],
})
export class RecentlyViewedFacetComponent implements OnInit, OnDestroy {
    private dialog = inject(MatDialog);
    private bo = inject(BreakpointObserver);
    private recentlyViewedService = inject(RecentlyViewedService);
    private initService = inject(CoveoInitService);
    private searchEngineService = inject(SearchEngineService);
    private router = inject(Router);

    @Input() title: string = 'Recently viewed';
    @Input() maxItems: number = 10;
    @Output() afterItemClick = new EventEmitter<void>();

    private recentlyViewedItems: RecentlyViewedItem[] = [];
    public facetValues: FacetValue[] = [];
    private subscription: Subscription = new Subscription();

    async ngOnInit(): Promise<void> {
        await this.initializeEngineAndSearch();
        this.loadRecentlyViewedItems();
        this.initializeController();

        // Subscribe to changes in recent views
        this.subscription.add(
            this.recentlyViewedService.recentViewsChanged$.subscribe(() => {
                this.loadRecentlyViewedItems();
                this.initializeController();
            }),
        );
    }

    /**
     * Initialize engines and then set up search
     */
    private async initializeEngineAndSearch(): Promise<void> {
        try {
            await this.initService.init();
        } catch (error) {
            console.error('Error initializing search engine:', error);
        }
    }

    /**
     * Unsubscribe from all subscriptions on destroy
     * @returns void
     */
    ngOnDestroy(): void {
        // Clean up subscriptions when component is destroyed
        this.subscription.unsubscribe();
    }

    /**
     * Load Recently Viewwed Items
     */
    private loadRecentlyViewedItems(): void {
        this.recentlyViewedItems = this.recentlyViewedService.recentViews;
    }

    /**
     * Re-map recently searched items into facets
     */
    private initializeController(): void {
        // Convert recently viewed items to facet values
        this.facetValues = this.recentlyViewedItems.map((item: RecentlyViewedItem) => ({
            value: item.title,
            numberOfResults: 1,
            state: 'idle' as const,
        }));
    }

    /**
     * Handle view click and navigate to the item URL in a new tab
     * @param title - The title of the viewed item
     */
    public onViewClick(title: string): void {
        const item = this.recentlyViewedItems.find((item: RecentlyViewedItem) => item.title === title);
        if (item?.url) {
            window.open(item.url, '_blank');

            // Capture Coveo click analytics
            if (item.result) {
                const { logDocumentOpen } = loadClickAnalyticsActions(this.searchEngineService.get());
                this.searchEngineService.get().dispatch(logDocumentOpen(item.result));
            }
        }

        // Emit event to notify parent component
        this.afterItemClick.emit();
    }

    /**
     * Open Modal Dialog with Headless Search Result data.
     * If already on search page, opens modal directly. Otherwise navigates to search page with query param.
     * @param title - The title of the recently viewed item
     */
    handleOpenDialog(title: string): void {
        const recentlyViewedItem = this.recentlyViewedItems.find((item: RecentlyViewedItem) => item.title === title);

        if (!recentlyViewedItem) {
            console.warn('Recently viewed item not found:', title);
            return;
        }

        // Emit event to notify parent component
        this.afterItemClick.emit();

        // Check if we're already on the search page
        const isOnSearchPage = this.router.url.includes(`/${GknRoutePath.SEARCH}`);

        if (isOnSearchPage) {
            // Already on search page, open modal directly
            this.openModal(recentlyViewedItem);
        } else {
            // Navigate to search page with query param to open modal
            this.router.navigate([`/${GknRoutePath.SEARCH}`], {
                queryParams: { openRecentlyViewed: title },
                queryParamsHandling: 'merge',
            });
        }
    }

    /**
     * Open the modal dialog for a recently viewed item
     * @param recentlyViewedItem - The item to display in the modal
     */
    private openModal(recentlyViewedItem: RecentlyViewedItem): void {
        // NOTE: Open Service Now Support cases as new tab. Cannot open to new modal due to
        // Service Now blocking their site from being iframed with CORs. Will have to revisit post MVP
        // NOTE: Open any Salesforce, Service Now links into new tab. Cannot be iframed in modals due to CORs policy
        // ex. https://genesys.my.site.com/customercare/
        // ex. https://genesyspartner.force.com/
        // ex. https://globalservicesportal.genesys.com
        if (
            recentlyViewedItem.url.indexOf('my.site.com') >= 0 ||
            recentlyViewedItem.url.indexOf('force.com') >= 0 ||
            recentlyViewedItem.url.indexOf('globalservicesportal') >= 0 ||
            recentlyViewedItem?.isSupportCase
        ) {
            // Open in new tab for these URLs that can't be iframed
            window.open(recentlyViewedItem?.url, '_blank');
        } else {
            firstValueFrom(
                this.bo.observe('(max-width: 1020px)').pipe(
                    map((state) => state.matches),
                    catchError((error) => {
                        console.error('Error determining screen size:', error);
                        return of(false);
                    }),
                ),
            )
                .then((isMobile) => {
                    this.dialog.open(IframeDialogComponent, {
                        width: isMobile ? '' : '80%',
                        maxWidth: '1400px',
                        height: isMobile ? '90%' : '80%',
                        autoFocus: false,
                        data: {
                            title: recentlyViewedItem?.title,
                            src: recentlyViewedItem?.url || '',
                            dataAnalytics: [this.getAnalyticsData(recentlyViewedItem?.title)],
                            showCopyButton: recentlyViewedItem?.isSupportCase ? false : true,
                            showOpenInNewTabButton: recentlyViewedItem?.isSupportCase ? false : true,
                        },
                    });
                })
                .catch((error) => {
                    console.error('Failed to open dialog:', error);
                    window.open(recentlyViewedItem?.url, '_blank');
                });
        }
    }

    /**
     * Generate analytics data attribute for recently viewed items
     * @param title - The title of the recently viewed item
     * @returns formatted analytics string
     */
    getAnalyticsData(title: string): string {
        if (title) {
            const trimmedTitle = title.trim();
            if (trimmedTitle.length > 0) {
                return 'recently-viewed-' + trimmedTitle.replace(/\s+/g, '-').toLowerCase();
            }
        }
        return 'recently-viewed-';
    }

    /**
     * Show More Facet value options
     */
    public showMore(): void {
        this.maxItems += 10;
        this.loadRecentlyViewedItems();
        this.initializeController();
    }

    /**
     * TODO: Fix
     * Show Less Facet value options
     */
    public showLess(): void {
        this.maxItems = Math.max(10, this.maxItems - 10);
        this.loadRecentlyViewedItems();
        this.initializeController();
    }

    /**
     * Can Show Less Facets
     * @returns boolean
     */
    public canShowLess(): boolean {
        return this.maxItems > 10 && this.recentlyViewedItems.length > 10;
    }

    /**
     * Can Show More Facets
     * @returns boolean
     */
    public canShowMore(): boolean {
        return this.recentlyViewedItems.length > this.maxItems;
    }

    /**
     * Clear all recently viewed items
     */
    public clearSelections(): void {
        this.recentlyViewedService.clearAllViews();
        this.recentlyViewedItems = [];
        this.facetValues = [];
    }
}
